import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { getOrganization, getOrganizationMembers } from "../../utils/Api";
import LoadingPage from "../../components/LoadingPage";
import { FileText, Users, Settings, Info } from "lucide-react";
import Members, { Member } from "./components/Members";
import Documents from "./components/Documents";
import OrganisationInfoModal from "../../modals/OrganisationInfoModal";

interface Organization {
  id: string;
  name: string;
  allow_mobile_upload: boolean;
  contact_phones: string[];
  contact_emails: string[];
  created_at: string;
}

type Tab = "documents" | "members";

const OrganizationView: React.FC = () => {
  const { organizationId } = useParams<{ organizationId: string }>();
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [userRole, setUserRole] = useState<string>("");
  const [members, setMembers] = useState<Member[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [isAddMemberModalOpen, setIsAddMemberModalOpen] =
    useState<boolean>(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState<boolean>(false);
  const [activeTab, setActiveTab] = useState<Tab>("documents");
  const navigate = useNavigate();

  useEffect(() => {
    if (!organizationId) return;

    let cancelled = false;

    const loadOrganizationData = async () => {
      try {
        setLoading(true);
        setError(null);

        const [orgResponse, membersResponse] = await Promise.all([
          getOrganization(organizationId),
          getOrganizationMembers(organizationId),
        ]);

        if (!cancelled) {
          setOrganization(orgResponse.data.organization);
          setUserRole(orgResponse.data.user_role);
          setMembers(membersResponse.data.members);
        }
      } catch (err: any) {
        if (!cancelled) {
          console.error("Failed to load organization:", err);
          setError(err.response?.data?.detail || "Failed to load organization");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    loadOrganizationData();

    return () => {
      cancelled = true;
    };
  }, [organizationId]);

  const loadOrganizationData = async (showLoading = true) => {
    try {
      if (showLoading) setLoading(true);
      setError(null);

      const [orgResponse, membersResponse] = await Promise.all([
        getOrganization(organizationId!),
        getOrganizationMembers(organizationId!),
      ]);

      setOrganization(orgResponse.data.organization);
      setUserRole(orgResponse.data.user_role);
      setMembers(membersResponse.data.members);
    } catch (err: any) {
      console.error("Failed to load organization:", err);
      setError(err.response?.data?.detail || "Failed to load organization");
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <LoadingPage
        fullScreen={false}
        variant="light"
        message="Loading organisation details"
      />
    );
  }

  if (error) {
    return (
      <div className="bg-gray-100 min-h-full">
        <div className="w-full mx-auto py-8 px-4 sm:py-12 sm:px-10">
          <div className="mb-4">
            <div
              className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded"
              role="alert"
            >
              {error}
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!organization) {
    return (
      <div className="bg-gray-100 min-h-full">
        <div className="w-full mx-auto py-8 px-4 sm:py-12 sm:px-10">
          <div className="mb-4">
            <div
              className="bg-yellow-100 border border-yellow-400 text-yellow-700 px-4 py-3 rounded"
              role="alert"
            >
              Organisation not found
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 h-full flex flex-col overflow-hidden">
      <div className="w-full mx-auto pt-6 px-4 sm:pt-12 sm:px-10 flex-shrink-0">
        <div className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <h3 className="text-2xl font-semibold">{organization.name}</h3>
            <button
              onClick={() => setIsInfoModalOpen(true)}
              className="p-2 text-gray-700 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
              aria-label="Organisation info"
            >
              <Info size={20} />
            </button>
            {userRole.toLowerCase() === "admin" && (
              <button
                onClick={() =>
                  navigate(`/home/organization/${organizationId}/settings`)
                }
                className="ml-auto p-2 text-gray-700 hover:text-gray-600 hover:bg-gray-200 rounded-lg transition-colors"
                aria-label="Organisation settings"
              >
                <Settings size={20} />
              </button>
            )}
          </div>
          <p className="text-gray-500">
            Manage your organisation documents and members
          </p>
        </div>

        {/* tabs */}
        <div className="border-b border-gray-200">
          <nav className="flex gap-4 sm:gap-8 pt-4 sm:pt-6">
            <button
              onClick={() => setActiveTab("documents")}
              className={`flex items-center gap-2 pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "documents"
                  ? "border-[#1d365d] text-[#1d365d]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <FileText className="w-4 h-4" />
              Documents
            </button>
            <button
              onClick={() => setActiveTab("members")}
              className={`flex items-center gap-2 pb-3 px-1 border-b-2 font-medium text-sm transition-colors ${
                activeTab === "members"
                  ? "border-[#1d365d] text-[#1d365d]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Users className="w-4 h-4" />
              Members
            </button>
          </nav>
        </div>
      </div>

      {/* tab content */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-10 pt-4 sm:pt-6 pb-8 sm:pb-12">
        {activeTab === "documents" && (
          <Documents
            organizationId={organizationId!}
            userRole={userRole}
            members={members}
          />
        )}

        {activeTab === "members" && (
          <Members
            organizationId={organizationId!}
            organizationName={organization.name}
            userRole={userRole}
            members={members}
            onMembersChange={() => loadOrganizationData(false)}
            isAddMemberModalOpen={isAddMemberModalOpen}
            setIsAddMemberModalOpen={setIsAddMemberModalOpen}
          />
        )}
      </div>

      <OrganisationInfoModal
        isOpen={isInfoModalOpen}
        setIsOpen={setIsInfoModalOpen}
        organizationName={organization.name}
        contactPhones={organization.contact_phones ?? []}
        contactEmails={organization.contact_emails ?? []}
        members={members}
      />
    </div>
  );
};

export default OrganizationView;
